<template>
  <div class="installment-list">
    <h1>
      {{ $t('InstallmentsList-Title') }}
    </h1>
    <p v-if="payBeforeDate" class="pay-before-subtitle">
      <span v-if="payBeforeDate.isOverdue" class="overdue-label">
        {{ payBeforeDatePrefix }}
      </span>
      <span v-else class="pay-before-label">
        {{ t("pay-before") }}
      </span>
      {{ payBeforeDate.datetime }}
    </p>
    <ul :class="{ 'margin-top-zero': payBeforeDate }">
      <li v-for="(i, index) of installmentsAlwaysAvailable" :key="index">
        <CardItem
          :title="i.label"
          :subtitle="i.subtitle"
          :right-text="computeAmountDisplay(i.amount)"
          :right-text-bold="true"
          @click="handleInstallmentClick(i)"
        />
      </li>
    </ul>
    <button v-if="!hideMoreOptions" class="installment-list-toggle-button" @click="toggleConditionalInstallments">
      {{ t("superpacman-more-options") }}
    </button>
    <ul v-if="showConditionalInstallments && !hideMoreOptions">
      <li v-for="(i, index) of installmentsConditional" :key="index">
        <CardItem
          :title="i.label"
          :right-text="computeAmountDisplay(i.amount)"
          :right-text-bold="true"
          @click="handleInstallmentClick(i)"
        />
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { FinanceProductTypes, ISuperPacmanInstallment, ISuperPacmanInstallmentItem } from "@/types/ISuperPacman";
import CardItem from "@/components/CardItem.vue";
import { useI18n } from "vue-i18n";
import { formatPayBeforeDateTime, getDaysInMonth } from "@/shared-helpers/date-time.helper";
import { useStore } from "vuex";
import { computedAmountValue, translateDaysPaidToLabel } from "@/shared-helpers/language.helper";
import { IInstallmentDetail } from "@/types/IInstallment";
import { IPayOptionsState } from "@/types/IPayOptionsState";
import { Countries } from "@/types/Countries";

export default defineComponent({
  name: "SuperPacmanInstallmentList",
  components: {
    CardItem,
  },
  props: {
    installmentDetails: {
      type: Object as PropType<ISuperPacmanInstallment>,
      required: true,
    },
    kasnetQrAvailable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["select-installment-item", "other-amount"],
  setup(props) {
    const { t, locale } = useI18n();
    const store = useStore<{ payOptions: IPayOptionsState }>();

    const computeAmountDisplay = (amount: string): string => {
      if (Number(amount) === 0) return "";
      return computedAmountValue(Number(amount));
    };

    const installmentsAlwaysAvailable: ISuperPacmanInstallmentItem[] = [];
    const installmentsConditional: ISuperPacmanInstallmentItem[] = [];

    const payBeforeDate = computed(() => {
      const timestamp = Number(props.installmentDetails.validThrough?.currentValidThrough);

      if (!timestamp) return null;

      const isOverdue = Date.now() > timestamp * 1000;
      const datetime = formatPayBeforeDateTime(timestamp);
      return { datetime, isOverdue };
    });

    // needed to reference the value, because for brazil BMP customers, we have to modify the CL enabled property
    const installment = ref<ISuperPacmanInstallment>(props.installmentDetails);

    // for Brazil customers, due to legal reasons:
    // if the subProductVersion == BMP, treat the customer as if he doesnt have CL enabled (VPOP-337)
    if (
      installment.value.financeProduct.country === Countries.BRAZIL &&
      installment.value.financeProduct.subProductVersion === "BMP"
    ) {
      installment.value.cumulativeBalance.enabled = false;
    }

    const isFEv1Mexico = computed(
      () =>
        installment.value.financeProduct.financeProductType === FinanceProductTypes.FEv1 &&
        installment.value.financeProduct.country === Countries.MEXICO,
    );

    const hideMoreOptions = computed(
      () =>
        (installment.value.cumulativeBalance.enabled ?? installment.value.cumulativeBalance.cumulativeBalanceEnabled) ||
        ([Countries.COLOMBIA.toString(), Countries.MEXICO.toString()].includes(
          installment.value.financeProduct.country,
        ) &&
          installment.value.financeProduct.financeProductType === FinanceProductTypes.FEv2) ||
        isFEv1Mexico.value ||
        installment.value.financeProduct.country === Countries.PERU ||
        (installment.value.financeProduct.country === Countries.INDONESIA &&
          installment.value.financeProduct.subProductVersion === "Rental"),
    );
    const showConditionalInstallments = ref(false);

    const payBeforeDatePrefix = computed(() => {
      let art = "";

      if (locale.value === "pt-BR") {
        // in portuguese, the days of the week have gender, so the article varies
        art = "na";
        if (["sáb", "dom"].some((p) => payBeforeDate.value?.datetime.startsWith(p))) {
          art = "no";
        }
      }
      return t("overdue-on", { art });
    });

    return {
      t,
      store,
      translateDaysPaidToLabel,
      computeAmountDisplay,
      installment,
      isFEv1Mexico,
      showConditionalInstallments,
      hideMoreOptions,
      payBeforeDate,
      payBeforeDatePrefix,
    };
  },
  computed: {
    installmentsAlwaysAvailable(): ISuperPacmanInstallmentItem[] {
      return this.returnInstallmentsAlwaysAvailable(this.installment, this.t);
    },
    installmentsConditional(): ISuperPacmanInstallmentItem[] {
      if (this.installment.cumulativeBalance.enabled) {
        return [];
      }
      return this.returnConditionalInstallments(this.installment, this.t);
    },
  },
  methods: {
    toggleConditionalInstallments() {
      this.showConditionalInstallments = !this.showConditionalInstallments;
    },
    handleInstallmentClick(installment: ISuperPacmanInstallmentItem) {
      if (installment.amount == "0") {
        this.$emit("other-amount");
      } else {
        const baseInstallmentDetail: IInstallmentDetail = {
          amount: Number(installment.amount),
          term: installment.originalLabel,
          isOverPayment: false,
          days: 0,
        };
        const acceptanceToken = this.store.state.payOptions.selectedInstallment?.acceptanceToken;
        this.store.dispatch("payOptions/setSelectedInstallment", baseInstallmentDetail);
        this.store.dispatch("payOptions/setWompiAcceptanceToken", acceptanceToken);
        this.store.dispatch("payOptions/setSuperPacmanAmount", Number(installment.amount));
        this.$emit("select-installment-item", installment);
      }
    },
    handleSelectInstallment(selectedInstallmentItem: ISuperPacmanInstallmentItem) {
      this.$emit("select-installment-item", selectedInstallmentItem);
    },
    handleOtherAmount() {
      this.$emit("other-amount");
    },
    returnInstallmentsAlwaysAvailable(
      installmentDetails: ISuperPacmanInstallment,
      t: (key: string) => string,
    ): ISuperPacmanInstallmentItem[] {
      const installmentsAlwaysAvailable: ISuperPacmanInstallmentItem[] = [];

      if (installmentDetails.daysToAmount.length > 0) {
        const remainingBalance: ISuperPacmanInstallmentItem = {
          originalLabel: "superpacman-remaining-balance",
          label: t("superpacman-remaining-balance"),
          amount: installmentDetails.financeProduct.fullPayoffAmount,
          currency: installmentDetails.financeProduct.currency,
        };

        // PYMT-1823: FEv1 MX - flat list of fixed installment options, no "Other Amount"
        // Deduplicate by originalLabel so devices with many daysToAmount entries (e.g.
        // cumulativeBalanceEnabled=true) show at most one row per label bucket (week, week2,
        // week3, month). translateDaysPaidToLabel maps any daysPaid >= 28 to "month", so
        // without deduplication multiple "1 mes" rows appear for the same bucket.
        if (this.isFEv1Mexico) {
          // Use canonical weekly/monthly daysPaid values only (7, 14, 21, <daysInMonth>).
          // The month bucket must match the actual number of days in the current calendar
          // month: 28/29 for February, 30 for Apr/Jun/Sep/Nov, 31 for all other months.
          // sp/installments always provides an entry for that exact day count.
          // All other entries (e.g. a 28-day entry in a 30-day month, or 37, 44, …) are
          // excluded to avoid mislabeling and OpenPay reference creation failures.
          const MX_CANONICAL_DAYS = new Set([7, 14, 21, getDaysInMonth()]);
          const seenLabels = new Set<string>();
          installmentDetails.daysToAmount
            .filter((value) => MX_CANONICAL_DAYS.has(value.daysPaid))
            .forEach((value) => {
              const [originalLabel, label] = this.translateDaysPaidToLabel(value.daysPaid, false, t);
              if (originalLabel && !seenLabels.has(originalLabel)) {
                seenLabels.add(originalLabel);
                installmentsAlwaysAvailable.push({
                  originalLabel: originalLabel as string,
                  label: label as string,
                  amount: value.amount,
                  currency: installmentDetails.financeProduct.currency,
                });
              }
            });
          installmentsAlwaysAvailable.push(remainingBalance);
          return installmentsAlwaysAvailable;
        }

        const [minimumAmountOriginalLabel, minimumAmountLabel, subtitle] = this.translateDaysPaidToLabel(
          installmentDetails.daysToAmount[0].daysPaid,
          true,
          t,
        );
        // We have talked with Corey, Sara and Ricky and we know that the first entry in the daysToAmount list is always going to be the minimum payment
        const minimumPayment: ISuperPacmanInstallmentItem = {
          label: minimumAmountLabel,
          subtitle: subtitle,
          amount: installmentDetails.daysToAmount[0].amount,
          currency: installmentDetails.financeProduct.currency,
          originalLabel: minimumAmountOriginalLabel,
        };
        /*
          Amount must be equal to "0". This triggers the "other-amount" event in handleInstallmentClick.
        */
        const otherAmount: ISuperPacmanInstallmentItem = {
          originalLabel: "superpacman-other-amount",
          label: t("superpacman-other-amount"),
          amount: "0",
          currency: installmentDetails.financeProduct.currency,
        };
        installmentsAlwaysAvailable.push(minimumPayment);
        installmentsAlwaysAvailable.push(remainingBalance);
        if (
          (installmentDetails.cumulativeBalance.enabled &&
            installmentDetails.financeProduct.country !== Countries.INDONESIA &&
            installmentDetails.financeProduct.country !== Countries.PERU) ||
          (installmentDetails.financeProduct.country === Countries.INDONESIA &&
            installmentDetails.financeProduct.subProductVersion === "Rental") ||
          ([Countries.COLOMBIA.toString(), Countries.MEXICO.toString()].includes(
            installmentDetails.financeProduct.country,
          ) &&
            installmentDetails.financeProduct.financeProductType === FinanceProductTypes.FEv2) ||
          (installmentDetails.financeProduct.country === Countries.PERU && this.kasnetQrAvailable)
        ) {
          installmentsAlwaysAvailable.push(otherAmount);
        }
        if (
          // VPOP-385
          installmentDetails.financeProduct.country === Countries.BRAZIL &&
          installmentDetails.financeProduct.subProductVersion === "Subscription"
        ) {
          const twoAndFourWeeksPayments = installmentDetails.daysToAmount.filter((value) =>
            [14, 28, 29, 30, 31].includes(value.daysPaid),
          );
          // Insert in reverse order to avoid index shifting issues
          for (let i = twoAndFourWeeksPayments.length - 1; i >= 0; i--) {
            const payment = twoAndFourWeeksPayments[i];
            const [originalLabel, label] = this.translateDaysPaidToLabel(payment.daysPaid, false, t);
            if (installmentsAlwaysAvailable.find((item) => item.originalLabel === originalLabel)) {
              // if the item is already in the list, skip it
              continue;
            }
            installmentsAlwaysAvailable.splice(1, 0, {
              originalLabel: originalLabel,
              label: label,
              amount: payment.amount,
              currency: installmentDetails.financeProduct.currency,
            });
          }
        }
      } else if (
        installmentDetails.financeProduct.fullPayoffAmount === installmentDetails.financeProduct.minimumPaymentAmount
      ) {
        installmentsAlwaysAvailable.push({
          originalLabel: "superpacman-remaining-balance",
          label: t("superpacman-remaining-balance"),
          amount: installmentDetails.financeProduct.fullPayoffAmount,
          currency: installmentDetails.financeProduct.currency,
        });
      }

      return installmentsAlwaysAvailable;
    },
    returnConditionalInstallments(
      installmentDetails: ISuperPacmanInstallment,
      t: (key: string) => string,
    ): ISuperPacmanInstallmentItem[] {
      const conditionalInstallments: ISuperPacmanInstallmentItem[] = [];

      // FEv1 MX: all options shown in the always-available flat list, nothing conditional
      if (this.isFEv1Mexico) {
        return conditionalInstallments;
      }

      /*
        Amount must be equal to "0". This triggers the "other-amount" event in handleInstallmentClick.
      */
      const otherAmount: ISuperPacmanInstallmentItem = {
        originalLabel: "superpacman-other-amount",
        label: t("superpacman-other-amount"),
        amount: "0",
        currency: installmentDetails.financeProduct.currency,
      };

      const weeklyPayments: ISuperPacmanInstallmentItem[] = [];

      installmentDetails.daysToAmount.forEach((value, index) => {
        if (index == 0) {
          // We skip the first index because it's the minimum amount and we have already processed it in the returnInstallmentsAlwaysAvailable function.
          return;
        }

        let label = "";
        let originalLabel = "";

        if (installmentDetails.financeProduct.country === Countries.BRAZIL) {
          // this will work only for Brazil, have to confirm that other countries can work like this
          const totalWeeks = Math.floor(value.daysPaid / 7);

          originalLabel = "week";
          label = `1 ${t("week")}`;

          if (totalWeeks > 1) {
            originalLabel = `week${totalWeeks}`;
            label = `${totalWeeks} ${t("weeks")}`;
          }
        } else {
          if (value.daysPaid >= 28) {
            // This could potentially not work for February
            label = t("month");
            originalLabel = "month";
          } else {
            label = t(DaysToString[value.daysPaid]);
            originalLabel = DaysToString[value.daysPaid];
          }
        }

        weeklyPayments.push({
          originalLabel: originalLabel,
          label: label,
          amount: value.amount,
          currency: installmentDetails.financeProduct.currency,
        } as ISuperPacmanInstallmentItem);
      });

      const creditLinePayoffAmount = Number(installmentDetails.financeProduct.creditLinePayoffAmount);
      if (
        [FinanceProductTypes.FEv2, FinanceProductTypes.JointLoanLine].includes(
          installmentDetails.financeProduct.financeProductType,
        ) &&
        creditLinePayoffAmount > 0 &&
        (installmentDetails.financeProduct.country !== Countries.INDONESIA ||
          installmentDetails.financeProduct.subProductVersion === "Rental")
      ) {
        conditionalInstallments.push(otherAmount);
      }

      if (
        [FinanceProductTypes.FEv1, FinanceProductTypes.JointLoanLine].includes(
          installmentDetails.financeProduct.financeProductType,
        )
      ) {
        conditionalInstallments.push(...weeklyPayments);
      }

      return conditionalInstallments;
    },
  },
});

enum DaysToString {
  week = 7,
  week2 = 14,
  week3 = 21,
  week4 = 28,
  month = 30,
}
</script>

<style scoped>
.installment-list {
  padding: 16px 16px 150px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.installment-list h1 {
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.26px;
  margin: 0;
}

.installment-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.pay-before-subtitle {
  color: var(--text-grey);
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.pay-before-label {
  color: var(--Primary-Green-1);
  font-weight: 500;
}

.overdue-label {
  color: #c87704;
  font-weight: 500;
}

ul.margin-top-zero {
  margin-top: 0;
}

.installment-list-toggle-button {
  border: none;
  background: none;
  text-align: right;
  color: var(--Primary-Green-1);
}
</style>
